import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Product } from '../types';
import AddToCartButton from './AddToCartButton';
import FavoriteButton from './FavoriteButton';
import { productImage, onImgError } from '../utils/images';
import { productUrl } from '../services/api';
import { useSiteConfig } from '../hooks/useConfig';

interface ProductCardProps { product: Product; index?: number; }

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex text-clay-500" aria-label={`Rated ${rating} of 5`}>
    {[...Array(5)].map((_, i) => (
      <svg key={i} xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5"
        viewBox="0 0 20 20" fill={rating >= i + 1 ? 'currentColor' : 'none'}
        stroke="currentColor" strokeWidth={rating >= i + 1 ? 0 : 1.5}>
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

const ProductCard: React.FC<ProductCardProps> = ({ product, index = 0 }) => {
  const to = productUrl(product);
  const hasDesc = product.description && !/^(Imported from Amazon ASIN|Newly imported listing)/i.test(product.description);
  const { complianceMode } = useSiteConfig();
  const listPrice = product.listPrice;
  const onSale = Boolean(listPrice && product.price > 0 && listPrice > product.price);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.3), ease: 'easeOut' }}
      className="organic-card group flex flex-col h-full"
    >
      <Link to={to} className="block relative overflow-hidden" aria-label={product.name}>
        {/* Perfect square, padded, contain: uniform thumbnails regardless of source aspect */}
        <div className="aspect-square w-full bg-white">
          <img src={productImage(product)} alt={product.name} loading="lazy" decoding="async"
            className="w-full h-full object-contain p-3 sm:p-4 group-hover:scale-105 transition-transform duration-700 ease-out"
            onError={onImgError()} />
        </div>
        <FavoriteButton productId={product.id} className="absolute top-3 left-3" />
        {onSale && (
          <span className="absolute bottom-3 left-3 rounded-full bg-clay-500 text-cream-50 text-[11px] font-semibold px-2.5 py-1 shadow-sm">
            Sale
          </span>
        )}
        {product.healthScore > 0 && (
          <div className="absolute top-3 right-3 flex flex-col items-center justify-center rounded-full h-12 w-12 bg-sage-600 text-cream-50 shadow-md">
            <span className="text-sm font-bold leading-none tabular-nums">{product.healthScore}</span>
            <span className="text-[8px] uppercase tracking-widest opacity-80">score</span>
          </div>
        )}
      </Link>

      <div className="p-4 sm:p-5 flex flex-col flex-grow">
        {product.rating > 0 && (
          <div className="mb-2 flex items-center gap-1.5">
            <Stars rating={product.rating} />
            <span className="text-xs text-earth-500 font-medium tabular-nums">{product.rating.toFixed(1)}</span>
          </div>
        )}
        <h3 className="font-display text-lg font-semibold text-earth-900 leading-snug mb-2 line-clamp-2">
          <Link to={to} className="hover:text-sage-700 transition-colors duration-300">{product.name}</Link>
        </h3>
        {product.brand && (
          <Link to={`/brands/${encodeURIComponent(product.brand.toLowerCase().replace(/[^a-z0-9]+/g, '-'))}`}
            className="text-xs text-sage-700 hover:text-sage-900 font-medium mb-2 inline-block">
            {product.brand}
          </Link>
        )}
        {hasDesc && <p className="text-earth-600 text-sm mb-4 line-clamp-2 leading-relaxed">{product.description}</p>}
        {product.tags && product.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {product.tags.slice(0, 3).map((t, i) => (<span key={i} className="chip">{t}</span>))}
          </div>
        )}
        <div className="mt-auto flex items-center justify-between pt-1 gap-2">
          <span className="text-sage-700 font-bold tabular-nums">
            {complianceMode ? (
              <span className="text-earth-500 text-sm font-medium">See price on Amazon</span>
            ) : product.price > 0 ? (
              <>
                ${product.price.toFixed(2)}
                {onSale && (
                  <span className="ml-1.5 text-earth-400 text-sm font-normal line-through">${listPrice!.toFixed(2)}</span>
                )}
              </>
            ) : (
              <span className="text-earth-500 text-sm font-medium">See price</span>
            )}
          </span>
          <AddToCartButton product={product} className="btn-sm" />
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
